import { makeAutoObservable, runInAction } from "mobx";
import type { RootStore } from "@intrasync/stores";
import type {
  LNICode,
  LNICodeData,
  EmployeeLNIAssignment,
  EmployeeLNIAssignmentData,
} from "../../types";

/**
 * LNI Codes Store
 * Manages Labor & Industries codes for Washington State workers compensation
 *
 * KEY FEATURES:
 * 1. Maintain LNI code master list with rates
 * 2. Assign/remove LNI codes to employees (Titan doesn't allow removal)
 * 3. Support temporary LNI code assignments per pay period
 */
class LNICodesStore {
  rootStore: RootStore;

  // UI State
  selectedCodeId: string | null = null;
  selectedAssignmentId: string | null = null;
  isCodeDialogOpen = false;
  isAssignmentDialogOpen = false;
  editingCode: Partial<LNICode> | null = null;
  editingAssignment: Partial<EmployeeLNIAssignment> | null = null;
  searchQuery = "";
  filterActive = true;
  filterOffSite: boolean | null = null;
  selectedWorkerId: string | null = null;
  isLoading = false;
  isSaving = false;

  // DataGrid configuration
  columns = [
    { key: "code", label: "Code", width: 100 },
    { key: "description", label: "Description", width: 250 },
    { key: "riskClass", label: "Risk Class", width: 120 },
    { key: "employeeRate", label: "EE Rate", width: 100, align: "right" as const },
    { key: "employerRate", label: "ER Rate", width: 100, align: "right" as const },
    { key: "isOffSite", label: "Off-Site", width: 80, align: "center" as const },
    { key: "isActive", label: "Active", width: 80, align: "center" as const },
  ];

  assignmentColumns = [
    { key: "workerName", label: "Employee", width: 200 },
    { key: "lniCode", label: "LNI Code", width: 100 },
    { key: "lniDescription", label: "Description", width: 200 },
    { key: "isPrimary", label: "Primary", width: 80, align: "center" as const },
    { key: "isTemporary", label: "Temporary", width: 100, align: "center" as const },
    { key: "effectiveDate", label: "Effective", width: 120 },
  ];

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // ==================== Computed Properties ====================

  get lniCodes(): LNICode[] {
    const store = this.rootStore.serviceStores?.["lni-codes"];
    return (store?.entities as LNICode[]) || [];
  }

  get assignments(): EmployeeLNIAssignment[] {
    const store = this.rootStore.serviceStores?.["employee-lni-assignments"];
    return (store?.entities as EmployeeLNIAssignment[]) || [];
  }

  get filteredCodes(): LNICode[] {
    let result = this.lniCodes;

    if (this.filterActive) {
      result = result.filter((c) => c.isActive);
    }

    if (this.filterOffSite !== null) {
      result = result.filter((c) => c.isOffSite === this.filterOffSite);
    }

    if (this.searchQuery) {
      const query = this.searchQuery.toLowerCase();
      result = result.filter(
        (c) =>
          c.code.toLowerCase().includes(query) ||
          c.description.toLowerCase().includes(query) ||
          c.riskClass.toLowerCase().includes(query)
      );
    }

    return result.sort((a, b) => a.code.localeCompare(b.code));
  }

  get filteredCount(): number {
    return this.filteredCodes.length;
  }

  get selectedCode(): LNICode | undefined {
    if (!this.selectedCodeId) return undefined;
    return this.lniCodes.find((c) => c.lniCodeId === this.selectedCodeId);
  }

  get gridData() {
    return this.filteredCodes.map((code) => ({
      id: code.lniCodeId,
      code: code.code,
      description: code.description,
      riskClass: code.riskClass,
      employeeRate: `$${code.employeeRate.toFixed(4)}`,
      employerRate: `$${code.employerRate.toFixed(4)}`,
      isOffSite: code.isOffSite ? "Yes" : "No",
      isActive: code.isActive ? "Active" : "Inactive",
    }));
  }

  get workerAssignments(): EmployeeLNIAssignment[] {
    if (!this.selectedWorkerId) return [];
    return this.assignments.filter((a) => a.workerId === this.selectedWorkerId);
  }

  get assignmentGridData() {
    return this.workerAssignments.map((assignment) => {
      const lniCode = this.lniCodes.find((c) => c.lniCodeId === assignment.lniCodeId);
      return {
        id: assignment.assignmentId,
        workerName: assignment.workerName || assignment.workerId,
        lniCode: lniCode?.code || assignment.lniCodeId,
        lniDescription: lniCode?.description || "",
        isPrimary: assignment.isPrimary ? "Yes" : "No",
        isTemporary: assignment.isTemporary ? "Temporary" : "Permanent",
        effectiveDate: assignment.effectiveDate,
      };
    });
  }

  // ==================== Actions ====================

  setSearchQuery = (query: string) => {
    this.searchQuery = query;
  };

  setFilterActive = (active: boolean) => {
    this.filterActive = active;
  };

  setFilterOffSite = (offSite: boolean | null) => {
    this.filterOffSite = offSite;
  };

  setSelectedCodeId = (id: string | null) => {
    this.selectedCodeId = id;
  };

  setSelectedWorkerId = (id: string | null) => {
    this.selectedWorkerId = id;
  };

  // Code Dialog Actions
  openCreateCodeDialog = () => {
    this.editingCode = {
      code: "",
      description: "",
      riskClass: "",
      baseRate: 0,
      employeeRate: 0,
      employerRate: 0,
      effectiveDate: new Date().toISOString().split("T")[0],
      isOffSite: false,
      isActive: true,
    };
    this.isCodeDialogOpen = true;
  };

  openEditCodeDialog = (code: LNICode) => {
    this.editingCode = { ...code };
    this.isCodeDialogOpen = true;
  };

  closeCodeDialog = () => {
    this.isCodeDialogOpen = false;
    this.editingCode = null;
  };

  updateEditingCode = (field: keyof LNICode, value: unknown) => {
    if (this.editingCode) {
      (this.editingCode as Record<string, unknown>)[field] = value;
    }
  };

  // Assignment Dialog Actions
  openCreateAssignmentDialog = (workerId?: string) => {
    this.editingAssignment = {
      workerId: workerId || this.selectedWorkerId || "",
      lniCodeId: "",
      isPrimary: false,
      isTemporary: false,
      effectiveDate: new Date().toISOString().split("T")[0],
    };
    this.isAssignmentDialogOpen = true;
  };

  openEditAssignmentDialog = (assignment: EmployeeLNIAssignment) => {
    this.editingAssignment = { ...assignment };
    this.isAssignmentDialogOpen = true;
  };

  closeAssignmentDialog = () => {
    this.isAssignmentDialogOpen = false;
    this.editingAssignment = null;
  };

  updateEditingAssignment = (field: keyof EmployeeLNIAssignment, value: unknown) => {
    if (this.editingAssignment) {
      (this.editingAssignment as Record<string, unknown>)[field] = value;
    }
  };

  // ==================== API Actions ====================

  loadData = async () => {
    this.isLoading = true;
    try {
      const codeStore = this.rootStore.serviceStores?.["lni-codes"];
      const assignmentStore = this.rootStore.serviceStores?.["employee-lni-assignments"];

      await Promise.all([
        codeStore?.find({ query: { $limit: 1000 } }),
        assignmentStore?.find({ query: { $limit: 5000 } }),
      ]);
    } catch (error) {
      console.error("Failed to load LNI data:", error);
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  saveCode = async (): Promise<boolean> => {
    if (!this.editingCode) return false;

    this.isSaving = true;
    try {
      const codeStore = this.rootStore.serviceStores?.["lni-codes"];

      const data: LNICodeData = {
        code: this.editingCode.code || "",
        description: this.editingCode.description || "",
        riskClass: this.editingCode.riskClass || "",
        baseRate: this.editingCode.baseRate || 0,
        employeeRate: this.editingCode.employeeRate || 0,
        employerRate: this.editingCode.employerRate || 0,
        effectiveDate: this.editingCode.effectiveDate || new Date().toISOString().split("T")[0],
        expirationDate: this.editingCode.expirationDate,
        isOffSite: this.editingCode.isOffSite ?? false,
        isActive: this.editingCode.isActive ?? true,
        notes: this.editingCode.notes,
      };

      if (this.editingCode.lniCodeId) {
        await codeStore?.patch(this.editingCode.lniCodeId, data);
      } else {
        await codeStore?.create(data);
      }

      this.closeCodeDialog();
      await this.loadData();
      return true;
    } catch (error) {
      console.error("Failed to save LNI code:", error);
      return false;
    } finally {
      runInAction(() => {
        this.isSaving = false;
      });
    }
  };

  deleteCode = async (codeId: string): Promise<boolean> => {
    try {
      const codeStore = this.rootStore.serviceStores?.["lni-codes"];
      await codeStore?.remove(codeId);
      await this.loadData();
      if (this.selectedCodeId === codeId) {
        this.selectedCodeId = null;
      }
      return true;
    } catch (error) {
      console.error("Failed to delete LNI code:", error);
      return false;
    }
  };

  saveAssignment = async (): Promise<boolean> => {
    if (!this.editingAssignment) return false;

    this.isSaving = true;
    try {
      const assignmentStore = this.rootStore.serviceStores?.["employee-lni-assignments"];

      const data: EmployeeLNIAssignmentData = {
        workerId: this.editingAssignment.workerId || "",
        lniCodeId: this.editingAssignment.lniCodeId || "",
        isPrimary: this.editingAssignment.isPrimary ?? false,
        isTemporary: this.editingAssignment.isTemporary ?? false,
        effectiveDate: this.editingAssignment.effectiveDate || new Date().toISOString().split("T")[0],
        expirationDate: this.editingAssignment.expirationDate,
        notes: this.editingAssignment.notes,
      };

      // If setting as primary, unset existing primary for this worker
      if (data.isPrimary) {
        const existingPrimary = this.assignments.find(
          (a) => a.workerId === data.workerId && a.isPrimary && a.assignmentId !== this.editingAssignment?.assignmentId
        );
        if (existingPrimary) {
          await assignmentStore?.patch(existingPrimary.assignmentId, { isPrimary: false });
        }
      }

      if (this.editingAssignment.assignmentId) {
        await assignmentStore?.patch(this.editingAssignment.assignmentId, data);
      } else {
        await assignmentStore?.create(data);
      }

      this.closeAssignmentDialog();
      await this.loadData();
      return true;
    } catch (error) {
      console.error("Failed to save LNI assignment:", error);
      return false;
    } finally {
      runInAction(() => {
        this.isSaving = false;
      });
    }
  };

  /**
   * Remove an LNI assignment from an employee
   * KEY FEATURE: Unlike Titan, we allow removing LNI codes
   */
  removeAssignment = async (assignmentId: string): Promise<boolean> => {
    try {
      const assignmentStore = this.rootStore.serviceStores?.["employee-lni-assignments"];
      await assignmentStore?.remove(assignmentId);
      await this.loadData();
      return true;
    } catch (error) {
      console.error("Failed to remove LNI assignment:", error);
      return false;
    }
  };

  /**
   * Set an LNI code as the primary for an employee
   */
  setPrimaryAssignment = async (assignmentId: string): Promise<boolean> => {
    const assignment = this.assignments.find((a) => a.assignmentId === assignmentId);
    if (!assignment) return false;

    try {
      const assignmentStore = this.rootStore.serviceStores?.["employee-lni-assignments"];

      // Unset existing primary
      const existingPrimary = this.assignments.find(
        (a) => a.workerId === assignment.workerId && a.isPrimary && a.assignmentId !== assignmentId
      );
      if (existingPrimary) {
        await assignmentStore?.patch(existingPrimary.assignmentId, { isPrimary: false });
      }

      // Set new primary
      await assignmentStore?.patch(assignmentId, { isPrimary: true });
      await this.loadData();
      return true;
    } catch (error) {
      console.error("Failed to set primary LNI assignment:", error);
      return false;
    }
  };

  // ==================== Utility Methods ====================

  getLNICodeLabel = (lniCodeId: string): string => {
    const code = this.lniCodes.find((c) => c.lniCodeId === lniCodeId);
    return code ? `${code.code} - ${code.description}` : lniCodeId;
  };

  isCodeExpired = (code: LNICode): boolean => {
    if (!code.expirationDate) return false;
    return new Date(code.expirationDate) < new Date();
  };

  getActiveCodesForDropdown = () => {
    return this.lniCodes
      .filter((c) => c.isActive && !this.isCodeExpired(c))
      .map((c) => ({
        value: c.lniCodeId,
        label: `${c.code} - ${c.description}`,
        isOffSite: c.isOffSite,
      }));
  };
}

export default LNICodesStore;
